import { useRef, useState } from 'react';
import { Animated, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, radii, spacing, type } from '~/ui/tokens';
import { useReducedMotion } from '~/hooks/useReducedMotion';

interface TooltipProps {
  label: string;
  children: React.ReactNode;
  // Set when the tooltip sits inside another pressable (a card that is
  // itself a button): the trigger keeps tap-to-toggle but takes no button
  // role, since HTML forbids a button inside a button.
  nested?: boolean;
}

// Hover-triggered on desktop (a mouse can hover); tap-to-toggle on touch,
// where hover doesn't exist. Self-contained (no portal) — sized for a short
// label anchored to small inline triggers, not for content near a viewport edge.
export function Tooltip({ label, children, nested = false }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const reduceMotion = useReducedMotion();
  const progress = useRef(new Animated.Value(0)).current;

  function show() {
    setVisible(true);
    if (reduceMotion) {
      progress.setValue(1);
      return;
    }
    Animated.spring(progress, { toValue: 1, friction: 8, tension: 120, useNativeDriver: true }).start();
  }

  function hide() {
    if (reduceMotion) {
      setVisible(false);
      progress.setValue(0);
      return;
    }
    Animated.timing(progress, { toValue: 0, duration: 120, useNativeDriver: true }).start(({ finished }) => {
      if (finished) setVisible(false);
    });
  }

  const isTouch = Platform.OS !== 'web' || (typeof window !== 'undefined' && 'ontouchstart' in window);

  return (
    <View style={styles.anchor}>
      <Pressable
        accessibilityRole={isTouch && !nested ? 'button' : undefined}
        accessibilityLabel={label}
        onHoverIn={!isTouch ? show : undefined}
        onHoverOut={!isTouch ? hide : undefined}
        onPress={isTouch ? () => (visible ? hide() : show()) : undefined}
      >
        {children}
      </Pressable>
      {visible ? (
        <Animated.View
          pointerEvents="none"
          style={[
            styles.bubble,
            {
              opacity: progress,
              transform: [
                { translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [4, 0] }) },
                { scale: progress.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1] }) },
              ],
            },
          ]}
        >
          <Text style={styles.bubbleText}>{label}</Text>
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  anchor: {
    position: 'relative',
    alignSelf: 'flex-start',
  },
  bubble: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    marginBottom: spacing(0.75),
    maxWidth: 220,
    backgroundColor: colors.ink,
    borderRadius: radii.sm,
    paddingHorizontal: spacing(1.25),
    paddingVertical: spacing(0.75),
    zIndex: 1000,
  },
  bubbleText: {
    fontFamily: fontFamily.regular,
    ...type.metadata,
    color: colors.white,
  },
});
